import { cn } from '@/lib/cn'

/**
 * State, shown as a word with a dot, not as a coloured pill.
 *
 * A pill is a fill, and this app has no fills. A 5px dot in a state colour next
 * to text carries the same information at a fraction of the visual
 * weight, which matters on a board showing forty weddings at once.
 */

export type Tone = 'neutral' | 'sage' | 'clay' | 'amber' | 'ink'

const DOT: Record<Tone, string> = {
  neutral: 'bg-[var(--color-rule-strong)]',
  sage: 'bg-sage',
  clay: 'bg-clay',
  amber: 'bg-amber',
  ink: 'bg-ink',
}

const TEXT: Record<Tone, string> = {
  neutral: 'text-taupe bg-[var(--color-wash-strong)]',
  sage: 'text-sage bg-[var(--color-sage-wash)]',
  clay: 'text-clay bg-[var(--color-clay-wash)]',
  amber: 'text-amber bg-[var(--color-amber-wash)]',
  ink: 'text-ink bg-[var(--color-wash-strong)]',
}

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone
  children: string
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold leading-tight',
        TEXT[tone],
        className,
      )}
    >
      <span aria-hidden className={cn('size-[6px] shrink-0 rounded-full', DOT[tone])} />
      {children}
    </span>
  )
}

/** The dot on its own, for a dense row where the word does not fit. */
export function StatusDot({ tone = 'neutral', title, className }: { tone?: Tone; title: string; className?: string }) {
  return <span title={title} aria-label={title} role="img" className={cn('inline-block size-[8px] shrink-0 rounded-full', DOT[tone], className)} />
}
